(() => {
  const parseMemberPriceConfig = (card) => {
    const configElement = card.querySelector('[data-rb-clp-member-price-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (_) {
      return null;
    }
  };

  const isValidDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

  const resolveMemberOverridePrice = (card, variantId, basePrice) => {
    const config = parseMemberPriceConfig(card);
    if (!config) return null;

    const variant = config.variants?.find((item) => String(item.id) === String(variantId));
    let rawOverrides = variant?.override == null ? config.productOverride : variant.override;
    if (typeof rawOverrides === 'string') {
      try {
        rawOverrides = JSON.parse(rawOverrides);
      } catch (_) {
        return null;
      }
    }
    const overrides = Array.isArray(rawOverrides) ? rawOverrides : rawOverrides ? [rawOverrides] : [];
    const quantity = card.dataset.isVirtualBundle === 'true'
      ? Math.max(1, Number(card.dataset.bundleQuantity) || 1)
      : 1;
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const override = overrides
      .filter((item) => {
        const value = Number(item?.value);
        const type = typeof item?.type === 'string' ? item.type.trim().toLowerCase() : '';
        const minQty = Number(item?.minQty ?? 1);
        return ['amount', 'percentage'].includes(type)
          && Number.isFinite(value) && value > 0
          && Number.isFinite(minQty) && minQty >= 1 && quantity >= minQty
          && (item?.startsAt == null || isValidDate(item.startsAt))
          && (item?.endsAt == null || isValidDate(item.endsAt))
          && !(item?.startsAt && item?.endsAt && item.startsAt > item.endsAt)
          && (!item?.startsAt || item.startsAt <= today)
          && (!item?.endsAt || item.endsAt >= today);
      })
      .sort((a, b) => Number(b.minQty ?? 1) - Number(a.minQty ?? 1))[0];

    if (!override) return null;
    const value = Number(override.value);
    if (String(override.type).toLowerCase() === 'percentage') {
      return Math.round(basePrice * (1 - Math.min(value, 100) / 100));
    }

    const discountCents = value * 100 / (override.perItem === true ? 1 : quantity);
    return Math.round(Math.max(0, basePrice - discountCents));
  };

  const formatMoney = (cents) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

  const applyMemberOverridePrice = (card, variantId, basePrice) => {
    if (window.EPIC_GARDENING?.isGreenhouseMember !== true) return;
    const overridePrice = resolveMemberOverridePrice(card, variantId, basePrice);
    if (overridePrice === null) return;

    const price = card.querySelector('[data-rb-clp-price]');
    const compare = card.querySelector('[data-rb-clp-compare]');
    const multiplier = card.dataset.isVirtualBundle === 'true'
      ? Math.max(1, Number(card.dataset.bundleQuantity) || 1)
      : 1;
    if (price) price.textContent = formatMoney(overridePrice * multiplier);
  };

  const applyMemberUpsellPrice = (card, variantId, basePrice) => {
    if (window.EPIC_GARDENING?.isGreenhouseMember === true) return;
    const overridePrice = resolveMemberOverridePrice(card, variantId, basePrice);
    const memberUpsellPrice = card.querySelector('[data-rb-clp-member-upsell-price]');
    const multiplier = card.dataset.isVirtualBundle === 'true'
      ? Math.max(1, Number(card.dataset.bundleQuantity) || 1)
      : 1;
    if (overridePrice !== null && memberUpsellPrice) memberUpsellPrice.textContent = formatMoney(overridePrice * multiplier);
  };

  const updateScrollState = (scroller) => {
    const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const hasOverflow = maximum > 2;
    scroller.classList.toggle('rb-clp__can-scroll', hasOverflow);
    scroller.classList.toggle('is-at-end', !hasOverflow || scroller.scrollLeft >= maximum - 2);
    return { maximum, hasOverflow };
  };

  const setupDragScrolling = (scroller) => {
    if (scroller.dataset.rbClpDragReady === 'true') return;
    scroller.dataset.rbClpDragReady = 'true';
    scroller.querySelectorAll('img, a').forEach((element) => {
      element.setAttribute('draggable', 'false');
    });

    let activePointer = null;
    let startX = 0;
    let startScrollLeft = 0;
    let didDrag = false;
    let suppressClick = false;

    const settleShortDrag = () => {
      const item = scroller.querySelector('[data-rb-clp-card], .rb-clp__size-card');
      if (!item) return;
      const styles = window.getComputedStyle(scroller);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const step = item.getBoundingClientRect().width + gap;
      const distance = scroller.scrollLeft - startScrollLeft;
      if (Math.abs(distance) < 24 || Math.abs(distance) >= step / 2) return;
      const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const target = Math.max(0, Math.min(maximum, startScrollLeft + Math.sign(distance) * step));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scroller.scrollTo({ left: target, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    const finishDrag = (event) => {
      if (event.pointerId !== activePointer) return;
      if (didDrag) settleShortDrag();
      if (scroller.hasPointerCapture(activePointer)) scroller.releasePointerCapture(activePointer);
      activePointer = null;
      scroller.classList.remove('is-dragging');
      if (didDrag) {
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 0);
      }
    };

    scroller.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (!scroller.classList.contains('rb-clp__can-scroll')) return;
      if (event.target.closest('button, input, select, textarea')) return;
      activePointer = event.pointerId;
      startX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
      didDrag = false;
      scroller.setPointerCapture(activePointer);
      scroller.classList.add('is-dragging');
    });

    scroller.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointer) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 4) didDrag = true;
      if (!didDrag) return;
      event.preventDefault();
      scroller.scrollLeft = startScrollLeft - distance;
    });

    scroller.addEventListener('pointerup', finishDrag);
    scroller.addEventListener('pointercancel', finishDrag);
    scroller.addEventListener('dragstart', (event) => event.preventDefault());
    scroller.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);
  };

  const setupScroller = (row) => {
    if (row.dataset.rbClpScrollReady === 'true') return;

    const scroller = row.querySelector('[data-rb-clp-scroller]');
    const controls = row.querySelector('[data-rb-clp-scroll-controls]');
    const previous = row.querySelector('[data-rb-clp-scroll-previous]');
    const next = row.querySelector('[data-rb-clp-scroll-next]');
    if (!scroller || !controls || !previous || !next) return;

    row.dataset.rbClpScrollReady = 'true';

    const updateControls = () => {
      const { maximum, hasOverflow } = updateScrollState(scroller);
      controls.hidden = !hasOverflow;
      previous.disabled = !hasOverflow || scroller.scrollLeft <= 2;
      next.disabled = !hasOverflow || scroller.scrollLeft >= maximum - 2;
    };

    const scrollOneCard = (direction) => {
      const card = scroller.querySelector('[data-rb-clp-card]');
      if (!card) return;
      const styles = window.getComputedStyle(scroller);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scroller.scrollBy({
        left: direction * (card.getBoundingClientRect().width + gap),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    };

    setupDragScrolling(scroller);
    previous.addEventListener('click', () => scrollOneCard(-1));
    next.addEventListener('click', () => scrollOneCard(1));
    scroller.addEventListener('scroll', updateControls, { passive: true });
    scroller.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      scrollOneCard(event.key === 'ArrowLeft' ? -1 : 1);
    });

    if ('ResizeObserver' in window) new ResizeObserver(updateControls).observe(scroller);
    requestAnimationFrame(updateControls);
  };

  const setupFinderScroller = (scroller) => {
    if (scroller.dataset.rbClpScrollReady === 'true') return;
    scroller.dataset.rbClpScrollReady = 'true';
    const update = () => updateScrollState(scroller);
    setupDragScrolling(scroller);
    scroller.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(scroller);
    requestAnimationFrame(update);
  };

  const setupScrollers = (scope = document) => {
    scope.querySelectorAll('[data-rb-clp-row]').forEach(setupScroller);
    scope.querySelectorAll('[data-rb-clp-finder-scroller]').forEach(setupFinderScroller);
  };

  const setupFinderNavigation = (scope = document) => {
    scope.querySelectorAll('[data-raised-beds-clp]').forEach((section) => {
      if (section.dataset.rbClpFinderReady === 'true') return;
      section.dataset.rbClpFinderReady = 'true';

      const cards = Array.from(section.querySelectorAll('[data-rb-clp-size-card]'));
      if (!cards.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const setActive = (activeCard) => {
        cards.forEach((card) => {
          const isActive = card === activeCard;
          card.classList.toggle('is-active', isActive);
          if (isActive) card.setAttribute('aria-current', 'location');
          else card.removeAttribute('aria-current');
        });
      };

      const highlightTarget = (target) => {
        target.classList.remove('is-targeted');
        requestAnimationFrame(() => target.classList.add('is-targeted'));
        window.setTimeout(
          () => target.classList.remove('is-targeted'),
          reducedMotion.matches ? 150 : 900
        );
      };

      const initialCard = cards.find((card) => card.hash === window.location.hash);
      if (initialCard) setActive(initialCard);

      cards.forEach((card) => {
        card.addEventListener('click', (event) => {
          const destination = card.getAttribute('href');
          if (!destination || !destination.startsWith('#')) return;

          const target = section.querySelector(destination);
          if (!target) return;

          event.preventDefault();
          setActive(card);
          if (window.location.hash !== destination) history.pushState(null, '', destination);

          let observer;
          const revealTarget = () => {
            if (observer) observer.disconnect();
            highlightTarget(target);
          };

          if (!reducedMotion.matches && 'IntersectionObserver' in window) {
            observer = new IntersectionObserver((entries) => {
              if (entries.some((entry) => entry.isIntersecting)) revealTarget();
            }, { threshold: 0.15 });
            observer.observe(target);
          } else {
            revealTarget();
          }

          target.scrollIntoView({
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
            block: 'start',
          });
        });
      });
    });
  };

  const cleanCustomPreorderCard = (card) => {
    const form = card.querySelector('form[data-type="add-to-cart-form"]');
    const isPurpleDotManaged = form?.classList.contains('x-pd-atc-form')
      || !!form?.querySelector('[name="properties[__releaseId]"], [name="properties[Purple Dot Pre-order]"]');
    if (isPurpleDotManaged) return;

    card.querySelectorAll('#pd-waitlist-info, .pd-waitlist-info, purple-dot-learn-more').forEach((element) => element.remove());
    card.querySelectorAll(
      '[name="properties[__pdDebug]"], [name="properties[__releaseId]"], [name="properties[Purple Dot Pre-order]"]'
    ).forEach((input) => input.remove());

    if (form) form.classList.remove('x-pd-atc-form--preorder');

    const submit = card.querySelector('[data-rb-clp-submit]');
    const buttonText = card.querySelector('[data-rb-clp-button-text]');
    if (!submit || !buttonText || submit.disabled || submit.classList.contains('loading')) return;

    const currentText = buttonText.textContent.trim();
    const activeSwatch = card.querySelector('[data-rb-clp-swatch].is-active');
    const isPreorder = card.dataset.customPreorderCard === 'true'
      && activeSwatch?.dataset.preorderActive === 'true';
    const label = isPreorder ? 'Pre-Order' : (submit.dataset.baseAddLabel || submit.dataset.addLabel || 'Add to cart');
    if (!/added|error/i.test(currentText) && currentText !== label) {
      buttonText.textContent = label;
    }
  };

  const setupCustomPreorderCards = (scope = document) => {
    scope.querySelectorAll('[data-custom-preorder-card="true"]').forEach((card) => {
      cleanCustomPreorderCard(card);
      if (card.dataset.customPreorderReady === 'true') return;
      card.dataset.customPreorderReady = 'true';

      const observer = new MutationObserver(() => cleanCustomPreorderCard(card));
      observer.observe(card, { childList: true, subtree: true });
    });
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-rb-clp-swatch]');
    if (!swatch) return;
    const card = swatch.closest('[data-rb-clp-card]');
    if (!card) return;
    card.querySelectorAll('[data-rb-clp-swatch]').forEach((button) => {
      const active = button === swatch;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    const image = card.querySelector('.rb-clp-card__image--primary');
    const hoverImage = card.querySelector('.rb-clp-card__image--hover');
    const price = card.querySelector('[data-rb-clp-price]');
    const compare = card.querySelector('[data-rb-clp-compare]');
    const discount = card.querySelector('[data-rb-clp-discount]');
    const color = card.querySelector('[data-rb-clp-color]');
    const input = card.querySelector('[data-rb-clp-variant-input]');
    const submit = card.querySelector('[data-rb-clp-submit]');
    const buttonText = card.querySelector('[data-rb-clp-button-text]');
    const form = card.querySelector('form[data-type="add-to-cart-form"]');
    const isPurpleDotManaged = form?.classList.contains('x-pd-atc-form');
    const available = swatch.dataset.available === 'true';
    if (image && swatch.dataset.image) { image.src = swatch.dataset.image; image.removeAttribute('srcset'); }
    if (hoverImage && swatch.dataset.hoverImage) {
      hoverImage.src = swatch.dataset.hoverImage;
      hoverImage.removeAttribute('srcset');
    }
    if (price) price.textContent = swatch.dataset.price;
    if (color) color.textContent = swatch.dataset.variantName;
    if (compare) { compare.textContent = swatch.dataset.compare; compare.hidden = !swatch.dataset.compare; }
    applyMemberOverridePrice(card, swatch.dataset.memberPricingVariantId, Number(swatch.dataset.memberPricingBasePrice || 0));
    applyMemberUpsellPrice(card, swatch.dataset.memberPricingVariantId, Number(swatch.dataset.memberPricingBasePrice || 0));
    if (discount) { discount.textContent = `${swatch.dataset.discount}% OFF`; discount.hidden = Number(swatch.dataset.discount) <= 0; }
    if (swatch.dataset.variantId) {
      card.querySelectorAll('a[href*="variant="]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', swatch.dataset.variantId);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }
    if (input) {
      const cartVariantId = swatch.dataset.cartVariantId || swatch.dataset.variantId;
      input.value = cartVariantId;
      input.setAttribute('value', cartVariantId);
      input.disabled = !available;
      if (submit) submit.dataset.variantId = cartVariantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (submit && !isPurpleDotManaged) submit.disabled = !available;
    if (buttonText && submit && !isPurpleDotManaged) {
      const isPreorder = card.dataset.customPreorderCard === 'true'
        && swatch.dataset.preorderActive === 'true';
      buttonText.textContent = available
        ? (isPreorder ? 'Pre-Order' : (submit.dataset.baseAddLabel || submit.dataset.addLabel || 'Add to cart'))
        : submit.dataset.soldLabel;
    }
    const preorderTimeline = card.querySelector('[data-custom-preorder-timeline], .rb-clp-card__custom-preorder-timeline');
    if (preorderTimeline && !isPurpleDotManaged) {
      preorderTimeline.hidden = swatch.dataset.preorderActive !== 'true';
      if (swatch.dataset.preorderTimeline) preorderTimeline.innerHTML = swatch.dataset.preorderTimeline;
    }
  });

  const setup = (scope = document) => {
    setupScrollers(scope);
    setupFinderNavigation(scope);
    setupCustomPreorderCards(scope);
    scope.querySelectorAll('[data-rb-clp-card]').forEach((card) => {
      const activeSwatch = card.querySelector('[data-rb-clp-swatch].is-active');
      if (!activeSwatch) return;
      applyMemberOverridePrice(card, activeSwatch.dataset.memberPricingVariantId, Number(activeSwatch.dataset.memberPricingBasePrice || 0));
      applyMemberUpsellPrice(card, activeSwatch.dataset.memberPricingVariantId, Number(activeSwatch.dataset.memberPricingBasePrice || 0));
    });
  };

  setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
